import React, { useEffect, useState, useRef } from "react";
import Zendesk from "react-zendesk";
import PropTypes from "prop-types";
import useStepper from "../../../hooks/useStepper";
import { useMutation } from "@apollo/client";
import { logoutUser } from "../../../graphql/mutations/logoutUser";
import { legacyAppRedirection, removeCookies } from "../../../helpers";
import { getCookie, setCookie } from "../../../utils/cookies";
import { Tooltip, IconButton, ClickAwayListener } from "@mui/material";
import Chat from "../../../assets/images/Chats.png";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import closeIcon from "../../../assets/images/X.png";

const ZENDESK_KEY = "11b49c5b-c889-447f-8850-950cb34365f3";

const Chatbtn = () => {
  const { personalDetails } = useStepper();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [logout] = useMutation(logoutUser);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false); // New state for tooltip visibility
  const buttonRef = useRef(null);
  const [showChatButton, setShowChatButton] = useState(true);
  const lastScrollTop = useRef(0);

  useEffect(() => {
    if (buttonRef?.current) {
      setIsTooltipOpen(buttonRef.current);
    }
  }, []);

  let logoutTimeoutId;

  const liveChatTrigger = () => {
    if (isChatOpen) {
      // Close the chat if it's currently open
      window?.zE && window?.zE("messenger", "hide");
    } else {
      // Open the chat if it's currently closed
      window?.zE && window?.zE("messenger", "show");
      window?.zE && window?.zE("messenger", "open");
    }

    setIsChatOpen(!isChatOpen); // Toggle chat state
  };

  const hendleAfterLoad = () => {
    if (window?.zE) {
      // Ensures that the Zendesk chat widget is hidden initially
      window?.zE("messenger", "hide");
      window?.zE("messenger", "set", "position", "bottomRight"); // Optional: Adjust position if needed
      window?.zE("messenger", "set", "hideOnClose", true); // Hide default close button

      // IMPORTANT: Listen for the chat closing event
      window?.zE("messenger:on", "close", () => {
        setIsChatOpen(false);
      });
    }
  };

  const hideZendeskLauncher = () => {
    // Monitor the DOM for changes and hide the launcher iframe when it appears
    const observer = new MutationObserver(async () => {
      const launcher = document.getElementById("launcher");
      if (launcher && launcher.parentNode) {
        await new Promise((resolve) => setTimeout(() => resolve(), 2000));
        if (launcher.parentNode) {
          launcher.parentNode.removeChild(launcher);
          observer.disconnect();
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    setTimeout(() => {
      observer.disconnect();
    }, 5000);
  };

  const logoutUserOnInactivity = () => {
    clearTimeout(logoutTimeoutId);
    setCookie("is-active-on-other-platform_avc", true);
    logoutTimeoutId = setTimeout(() => {
      const isActiveOnOtherPlatform =
        getCookie("is-active-on-other-platform") === true;

      if (isActiveOnOtherPlatform) {
        logoutUserOnInactivity();
        setCookie("is-active-on-other-platform_avc", false);
        return;
      }

      logout({
        onCompleted: (data) => {
          removeCookies();
          window.location.href = data?.destroy_session?.logout_url;
        },
        onError: () => {
          removeCookies();
          legacyAppRedirection(personalDetails?.sub_domain, "/");
        },
      });
    }, (process.env.REACT_APP_INACTIVITY_LOGOUT_TIMEOUT || 2) * 60 * 60 * 1000);
  };

  useEffect(() => {
    // Hide the Zendesk widget initially
    if (window?.zE) {
      window?.zE("messenger", "hide");
    }
  }, []);

  useEffect(() => {
    hideZendeskLauncher(); // Continuously monitor and hide the launcher

    let timeoutId;

    const hasLaunchedChat = sessionStorage.getItem("hasLaunchedChatAVC");

    if (!hasLaunchedChat) {
      // Auto-open the chat for first-time users
      setTimeout(() => {
        window?.zE && window?.zE("messenger", "open");
        window?.zE && window?.zE("messenger", "hide");
        sessionStorage.setItem("hasLaunchedChatAVC", "true");
      }, 2000); // slight delay to ensure widget is loaded
    }

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        liveChatTrigger();
      }, process.env.REACT_APP_INACTIVITY_TIMEOUT || 180000);
      logoutUserOnInactivity();
    };

    const events = [
      "mousedown",
      "mousemove",
      "keydown",
      "keypress",
      "scroll",
      "touchstart",
    ];

    events.forEach((event) => {
      document.addEventListener(event, resetTimer);
    });

    resetTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [personalDetails]);

  const handleButtonClick = () => {
    setIsTooltipOpen(false);
    liveChatTrigger();
  };

  const handleTooltipClose = () => {
    setIsTooltipOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollBottomReached =
        window.innerHeight + scrollTop >= document.body.offsetHeight;

      if (scrollBottomReached && scrollTop > lastScrollTop.current) {
        setShowChatButton(false);
        setIsTooltipOpen(false);
      } else if (scrollTop < lastScrollTop.current) {
        setShowChatButton(true);
      }

      lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Zendesk
        defer
        zendeskKey={ZENDESK_KEY}
        onLoaded={() => hendleAfterLoad()}
        widgetType="web_widget"
      />
      <ClickAwayListener onClickAway={handleTooltipClose}>
        <div
          className={`chat-animate-wrapper ${showChatButton ? "" : "hidden"}`}
        >
          <Tooltip
            open={isTooltipOpen}
            onClose={handleTooltipClose}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            placement="top-start"
            componentsProps={{
              tooltip: {
                sx: {
                  margin: 0,
                  padding: 0,
                },
                className: "custom-chat-tooltip",
              },
              popper: {
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, -3],
                    },
                  },
                ],
              },
            }}
            title={
              <>
                <IconButton onClick={handleTooltipClose} className="close-btn">
                  <img src={closeIcon} className="closeIcon-chat" />
                </IconButton>
                <span>Hi, need any help?</span>
              </>
            }
          >
            <button
              className="custom-chat-btn"
              onClick={() => {
                setIsTooltipOpen(!isTooltipOpen);
                handleButtonClick();
              }}
              ref={buttonRef}
            >
              {isChatOpen ? (
                <KeyboardArrowDownIcon className="chat-arrow-icon" />
              ) : (
                <img src={Chat} alt="Chat Icon" className="chat-icon" />
              )}
            </button>
          </Tooltip>
        </div>
      </ClickAwayListener>
    </>
  );
};

Chatbtn.propTypes = {
  trackingDetails: PropTypes.object,
};

export default Chatbtn;
